import { Controller } from 'stimulus'

// L'allowlist reprend celle de AnnouncementsHelper#announcement_article : l'aperçu
// garde exactement ce que le serveur garde. Seule différence assumée : un
// <figure data-shot="N"> sans capture est signalé au lieu d'être supprimé.
const ALLOWED_TAGS  = ['P', 'H3', 'UL', 'OL', 'LI', 'STRONG', 'EM', 'BR', 'BLOCKQUOTE', 'FIGURE', 'FIGCAPTION', 'IMG']
const ALLOWED_ATTRS = ['class', 'src', 'alt', 'loading']
const KILLED_TAGS   = ['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'TEMPLATE', 'NOSCRIPT']
const REDRAW_MS     = 120

export default class extends Controller {
  static targets = [
    'titleField', 'bodyField', 'articleField', 'categoryField', 'moduleField', 'slotInput',
    'cardBadges', 'cardTitle', 'cardBody', 'badges', 'title', 'lead', 'body', 'shots', 'empty',
    'extraSlots', 'extraList',
  ]

  connect() {
    this.blobs  = new Map()
    this.redraw = () => this.schedule()
    this.element.addEventListener('input', this.redraw)
    this.element.addEventListener('change', this.redraw)
    this.render()
  }

  disconnect() {
    this.element.removeEventListener('input', this.redraw)
    this.element.removeEventListener('change', this.redraw)
    clearTimeout(this.timer)
    this.blobs.forEach(({ url }) => URL.revokeObjectURL(url))
    this.blobs.clear()
  }

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.render(), REDRAW_MS)
  }

  render() {
    const title   = this.fieldValue('titleField')
    const body    = this.fieldValue('bodyField')
    const article = this.fieldValue('articleField')

    this.renderBadges(this.badgesTarget)
    this.renderBadges(this.cardBadgesTarget)

    this.titleTarget.textContent     = title || this.data.get('untitled')
    this.cardTitleTarget.textContent = title || this.data.get('untitled')
    this.cardBodyTarget.textContent  = body

    this.leadTarget.textContent = body
    this.leadTarget.hidden      = article.length > 0
    this.bodyTarget.hidden      = article.length === 0

    const shots = article.length > 0 ? this.renderArticle(article) : { filled: 0, total: 0, slots: [] }
    this.renderShots(shots)
    this.syncExtraSlots(shots.slots)
    this.emptyTarget.hidden = title.length > 0 || body.length > 0 || article.length > 0
  }

  renderArticle(html) {
    const holder = document.createElement('template')
    holder.innerHTML = html
    this.scrub(holder.content)
    const shots = this.fillFigures(holder.content)

    this.bodyTarget.innerHTML = ''
    this.bodyTarget.appendChild(holder.content)
    return shots
  }

  scrub(root) {
    root.querySelectorAll(KILLED_TAGS.join(',')).forEach(node => node.remove())

    // Comme `sanitize` côté serveur : la balise part, son contenu reste.
    root.querySelectorAll('*').forEach(node => {
      if (!ALLOWED_TAGS.includes(node.tagName)) return this.unwrap(node)

      Array.from(node.attributes).forEach(attr => {
        const kept = ALLOWED_ATTRS.includes(attr.name) ||
                     (node.tagName === 'FIGURE' && attr.name === 'data-shot')
        if (!kept) node.removeAttribute(attr.name)
      })
    })
  }

  unwrap(node) {
    const parent = node.parentNode
    if (!parent) return

    while (node.firstChild) parent.insertBefore(node.firstChild, node)
    parent.removeChild(node)
  }

  fillFigures(root) {
    const sources = this.slotSources()
    const figures = Array.from(root.querySelectorAll('figure[data-shot]'))
    const slots   = []
    let filled = 0

    figures.forEach(figure => {
      const slot    = parseInt(figure.getAttribute('data-shot'), 10)
      const caption = (figure.querySelector('figcaption') ? figure.querySelector('figcaption').textContent : '').trim()
      const src     = sources[String(slot)]
      if (!Number.isNaN(slot)) slots.push({ number: slot, caption: caption })

      figure.removeAttribute('data-shot')
      figure.className = 'announcement-article__figure'
      figure.innerHTML = ''

      if (src) {
        filled += 1
        const image = document.createElement('img')
        image.src = src
        image.alt = caption
        figure.appendChild(image)
      } else {
        figure.classList.add('announcement-article__figure--missing')
        figure.appendChild(this.missingShot(slot))
      }

      if (caption) {
        const figcaption = document.createElement('figcaption')
        figcaption.textContent = caption
        figure.appendChild(figcaption)
      }
    })

    return { filled: filled, total: figures.length, slots: slots }
  }

  missingShot(slot) {
    const box = document.createElement('div')
    box.className = 'bo-announcement-preview__missing'

    const number = document.createElement('span')
    number.className = 'bo-announcement-preview__missing-num'
    number.textContent = Number.isNaN(slot) ? '?' : slot
    box.appendChild(number)

    const label = document.createElement('span')
    label.textContent = this.data.get('missing')
    box.appendChild(label)

    return box
  }

  // Le formulaire ne rend un champ de dépôt que pour les emplacements de l'article
  // enregistré ; ceux qui viennent d'être écrits en reçoivent un ici, nommé comme
  // les autres, pour partir avec l'enregistrement (y compris à la création).
  syncExtraSlots(slots) {
    if (!this.hasExtraListTarget) return

    const wanted = new Map(slots.map(slot => [String(slot.number), slot.caption]))
    this.slotInputTargets
      .filter(input => !this.extraListTarget.contains(input))
      .forEach(input => wanted.delete(input.dataset.slot))

    Array.from(this.extraListTarget.children).forEach(item => {
      const slot = item.dataset.slot

      if (!wanted.has(slot)) {
        this.forget(slot)
        return item.remove()
      }

      item.querySelector('.bo-announcement-form__slot-caption').textContent =
        wanted.get(slot) || this.data.get('untitledShot')
      wanted.delete(slot)
    })

    wanted.forEach((caption, slot) => this.extraListTarget.appendChild(this.extraSlot(slot, caption)))
    this.extraSlotsTarget.hidden = this.extraListTarget.children.length === 0
  }

  extraSlot(slot, caption) {
    const item = document.createElement('li')
    item.className = 'bo-announcement-form__slot'
    item.dataset.slot = slot

    const number = document.createElement('span')
    number.className = 'bo-announcement-form__slot-num'
    number.textContent = slot
    item.appendChild(number)

    const main = document.createElement('div')
    main.className = 'bo-announcement-form__slot-main'

    const label = document.createElement('div')
    label.className = 'bo-announcement-form__slot-caption'
    label.textContent = caption || this.data.get('untitledShot')
    main.appendChild(label)

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.name = `announcement[slot_images][${slot}]`
    input.className = 'bo-announcement-form__slot-input'
    input.dataset.slot = slot
    input.dataset.target = 'announcements--preview.slotInput'
    main.appendChild(input)

    item.appendChild(main)
    return item
  }

  slotSources() {
    let sources = {}
    try {
      sources = JSON.parse(this.data.get('images') || '{}')
    } catch (e) {
      sources = {}
    }

    this.slotInputTargets.forEach(input => {
      const slot = input.dataset.slot
      const file = input.files && input.files[0]
      if (!file) {
        this.markFilled(input, false)
        return this.forget(slot)
      }

      const key   = [file.name, file.size, file.lastModified].join(':')
      const known = this.blobs.get(slot)
      if (!known || known.key !== key) {
        this.forget(slot)
        this.blobs.set(slot, { key: key, url: URL.createObjectURL(file) })
      }
      sources[slot] = this.blobs.get(slot).url
      this.markFilled(input, true)
    })

    return sources
  }

  // Sur un emplacement du formulaire, « déposé » dit qu'une capture est déjà
  // enregistrée : seuls ceux ajoutés ici suivent le fichier choisi.
  markFilled(input, filled) {
    if (!this.hasExtraListTarget || !this.extraListTarget.contains(input)) return

    const item = input.closest('li')
    if (item) item.classList.toggle('is-filled', filled)
  }

  forget(slot) {
    const known = this.blobs.get(slot)
    if (!known) return

    URL.revokeObjectURL(known.url)
    this.blobs.delete(slot)
  }

  renderBadges(holder) {
    const category = this.categoryFieldTarget
    const badge    = holder.querySelector('[data-badge="category"]')
    badge.className   = `announcement-badge announcement-badge--${category.value}`
    badge.textContent = this.selectedLabel(category)

    const module = holder.querySelector('[data-badge="module"]')
    const picked = this.hasModuleFieldTarget && this.moduleFieldTarget.value.length > 0
    module.hidden      = !picked
    module.textContent = picked ? this.selectedLabel(this.moduleFieldTarget) : ''
  }

  renderShots({ filled, total }) {
    this.shotsTarget.hidden = total === 0
    if (total === 0) return

    this.shotsTarget.textContent = `${filled}/${total} ${this.data.get('shots')}`
    this.shotsTarget.classList.toggle('is-complete', filled === total)
  }

  fieldValue(name) {
    if (!this[`has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`]) return ''

    return this[`${name}Target`].value.trim()
  }

  selectedLabel(select) {
    const option = select.options[select.selectedIndex]
    return option ? option.text : ''
  }
}
